import { dayText, galText } from "./accounts";
import type { Load, LoadDrivers, LoadResult, PickupLoad } from "./api";
import { callDay } from "./calls";

export const MAX_RESULT_GAL = 50_000;

/** A truck is 1 to 8 letters or digits. The server keeps it in capitals. */
export function truckError(truck: string): string {
  if (!truck.trim()) return "Enter the truck.";
  if (!/^[A-Za-z0-9]{1,8}$/.test(truck.trim())) return "Use 1 to 8 letters or digits for the truck, for example T7.";
  return "";
}

export const cleanTruck = (truck: string) => truck.trim().toUpperCase();

/** The default driver of a new load: the driver of today's route of the truck, else "" (the user). */
export function loadDriver(choice: LoadDrivers, truck: string): string {
  const driver = choice.truck_drivers?.[cleanTruck(truck)];
  return driver && choice.drivers?.some((person) => person.id === driver) ? driver : "";
}

export type ResultForm = { raw: string; pct: string };
export type ResultError = { field: "raw" | "pct"; text: string };

/** The processor result: raw gallons above 0, and a scrap percent from 0 to 100. "15%" is accepted. */
export function parseResult(form: ResultForm): { raw_gal: number; scrap_pct: number } | ResultError {
  const raw = Number(form.raw.trim());
  if (!form.raw.trim() || !Number.isFinite(raw) || raw <= 0) return { field: "raw", text: "Enter the raw gallons that the processor measured." };
  if (raw > MAX_RESULT_GAL) return { field: "raw", text: `Raw gallons cannot be more than ${MAX_RESULT_GAL.toLocaleString("en-US")}.` };
  const text = form.pct.trim().replace(/%$/, "").trim();
  const pct = Number(text);
  if (!text || !Number.isFinite(pct) || pct < 0 || pct > 100) return { field: "pct", text: "Enter a scrap percent from 0 to 100." };
  return { raw_gal: raw, scrap_pct: pct };
}

/** A result far from the pickups: less than half, or more than 1.5 times, their raw gallons (SO-1924). */
export const RESULT_LOW = 0.5;
export const RESULT_HIGH = 1.5;

/** The question before a result that is over the truck or far from the pickups, else "". */
export function resultWarning(raw: number, load: Pick<Load, "raw_gal" | "truck" | "truck_gal">): string {
  const again = "Press Save again to save it.";
  if (load.truck_gal && raw > load.truck_gal)
    return `${galText(raw)} is more than the ${galText(load.truck_gal)} of truck ${load.truck}. ${again}`;
  if (load.raw_gal > 0 && (raw < load.raw_gal * RESULT_LOW || raw > load.raw_gal * RESULT_HIGH))
    return `${galText(raw)} is far from the ${galText(load.raw_gal)} raw of the pickups on this load. ${again}`;
  return "";
}

/** The fields of the correction form start with the newest result. */
export const resultFields = (result: Pick<LoadResult, "raw_gal" | "scrap_pct"> | null): ResultForm =>
  result ? { raw: String(result.raw_gal), pct: String(result.scrap_pct) } : { raw: "", pct: "" };

export type LoadState = "approved" | "measured" | "open";

export function loadState(load: Pick<Load, "approval" | "result">): LoadState {
  if (load.approval) return "approved";
  return load.result ? "measured" : "open";
}

export const STATE_TEXT: Record<LoadState, string> = {
  approved: "Approved",
  measured: "Result recorded",
  open: "Open",
};

/** The measured scrap gallons: the raw gallons × the scrap percent, to 0.01 gal. */
export const measuredScrap = (result: { raw_gal: number; scrap_pct: number }) =>
  Math.round(result.raw_gal * result.scrap_pct) / 100;

/** The stops whose scrap the approval changes, with the old and the new scrap. */
export function previewChanges(load: Pick<Load, "stops" | "preview">): { pickup_id: string; from: number; to: number }[] {
  const scrap = load.preview?.scrap;
  if (!scrap) return [];
  return load.stops
    .filter((stop) => stop.pickup_id in scrap && scrap[stop.pickup_id] !== stop.scrap_gal)
    .map((stop) => ({ pickup_id: stop.pickup_id, from: stop.scrap_gal, to: scrap[stop.pickup_id] }));
}

/** "1 pickup", "2 pickups". */
export const countText = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The line under a pickup that is on a load: the approval correction names its cause. */
export function pickupLoadText(load: PickupLoad | null | undefined, timeZone: string): string | null {
  if (!load) return null;
  if (load.by_approval && load.approved_at) {
    return `Scrap set by load ${load.code} (approved ${dayText(callDay(load.approved_at, timeZone), true)})`;
  }
  return `On load ${load.code}`;
}

/** A pickup on an approved load has no correction (L-2). */
export const lockedBy = (load: PickupLoad | null | undefined): string | null =>
  load?.approved_at ? `On approved load ${load.code}` : null;

/** The loads that wait for a processor result of this user come first; the order is kept. */
export function needsResultFirst<T extends Pick<Load, "can_record_result" | "result" | "approval">>(loads: readonly T[]): T[] {
  const waits = (load: T) => load.can_record_result && !load.result && !load.approval;
  return [...loads.filter(waits), ...loads.filter((load) => !waits(load))];
}

/** The rows in the order of the first list; a new row goes first. A save does not move the rows. */
export function keepOrder<T extends { id: string }>(rows: readonly T[], first: readonly string[] | null): T[] {
  if (!first) return [...rows];
  const rank = new Map(first.map((id, index) => [id, index]));
  return [...rows].sort((a, b) => (rank.get(a.id) ?? -1) - (rank.get(b.id) ?? -1));
}

export function approvalQuestion(code: string, corrections: number): string {
  return `Approve load ${code}? This writes ${countText(corrections, "scrap correction")} and closes the load. You cannot undo it.`;
}

/** The line for the pickups on accounts that the role does not show. */
export function hiddenStopsText(load: Pick<Load, "stops" | "hidden_stops">): string {
  const n = load.hidden_stops;
  if (load.stops.length > 0)
    return n === 1 ? "1 more pickup is on an account that your role does not show." : `${n} more pickups are on accounts that your role does not show.`;
  return n === 1
    ? "1 pickup is on this load. Your role does not show its account."
    : `${n} pickups are on this load. Your role does not show their accounts.`;
}
